import { useId } from 'react'
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import type {
    HeadcountByCompany,
    HeadcountPayload,
    HeadcountScope,
    HeadcountScopeStats,
} from '../../../../../../server/controllers/portal/dashboard-card-payloads.js'
import './headcount-card.css'

/** Brand-ish colours per company; falls back to a small palette by index. */
const COMPANY_COLORS: Record<string, string> = { nope: 'var(--chart-1)', play: 'var(--chart-2)' }
const FALLBACK_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)']
const TOTAL_COLOR = 'var(--chart-5)'

const SCOPES: HeadcountScope[] = ['nope', 'play', 'np', 'total']

type ChartRow = { month: string; [series: string]: number | string }

const toChartRows = (series: HeadcountPayload['series']): ChartRow[] =>
    series.map((p) => ({ month: p.month.slice(5), total: p.total, ...p.byCompany }))

const TrendChart = ({ rows, companies }: { rows: ChartRow[]; companies: HeadcountByCompany[] }) => (
    <ResponsiveContainer width="100%" height={110}>
        <LineChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={28} allowDecimals={false} />
            {companies.map((co, i) => (
                <Line
                    key={co.slug}
                    type="monotone"
                    dataKey={co.slug}
                    name={co.name}
                    className={`portal-headcount-card__line portal-headcount-card__line--company portal-headcount-card__line--${co.slug}`}
                    stroke={COMPANY_COLORS[co.slug] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                />
            ))}
            <Line
                type="monotone"
                dataKey="total"
                className="portal-headcount-card__line portal-headcount-card__line--total"
                stroke={TOTAL_COLOR}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
            />
        </LineChart>
    </ResponsiveContainer>
)

const ScopePanel = ({ stats, lang }: { stats: HeadcountScopeStats; lang: Language }) => {
    const c = t.portal.dashboard.cards.headcount
    const trendColor =
        stats.trend > 0
            ? 'var(--portal-success)'
            : stats.trend < 0
              ? 'var(--portal-warning)'
              : undefined

    return (
        <>
            <span className="portal-card__value">{stats.total}</span>
            <span
                className="portal-card__subtext"
                style={trendColor ? { color: trendColor } : undefined}
            >
                {c.trend(stats.trend)(lang)}
            </span>
            <div className="portal-headcount-card__stats">
                <span>{c.joiners(stats.joiners)(lang)}</span>
                <span>{c.leavers(stats.leavers)(lang)}</span>
                <span>{c.turnover(Math.round(stats.turnoverRate * 100))(lang)}</span>
            </div>
        </>
    )
}

export const HeadcountCard = ({ data, lang }: { data: HeadcountPayload; lang: Language }) => {
    const c = t.portal.dashboard.cards.headcount
    const baseId = useId()
    const rows12 = toChartRows(data.series)
    const rows6 = rows12.slice(-6)
    const hasChart = rows12.length > 0

    // Two visually-hidden radio groups drive the company + period selectors via
    // CSS :has() — the dashboard cards avoid React hooks, so there is no JS
    // state here. Every scope panel and both chart windows are rendered; CSS
    // shows the selected one. `useId()` namespaces the radio name/id so two
    // copies of the card (e.g. DragOverlay during reorder) do not share state.
    return (
        <div className="portal-headcount">
            <input
                type="radio"
                name={`${baseId}-headcount-scope`}
                id={`${baseId}-headcount-scope-nope`}
                value="nope"
                className="portal-headcount__radio portal-headcount__radio--nope"
            />
            <input
                type="radio"
                name={`${baseId}-headcount-scope`}
                id={`${baseId}-headcount-scope-play`}
                value="play"
                className="portal-headcount__radio portal-headcount__radio--play"
            />
            <input
                type="radio"
                name={`${baseId}-headcount-scope`}
                id={`${baseId}-headcount-scope-np`}
                value="np"
                className="portal-headcount__radio portal-headcount__radio--np"
                defaultChecked
            />
            <input
                type="radio"
                name={`${baseId}-headcount-scope`}
                id={`${baseId}-headcount-scope-total`}
                value="total"
                className="portal-headcount__radio portal-headcount__radio--total"
            />
            <input
                type="radio"
                name={`${baseId}-headcount-period`}
                id={`${baseId}-headcount-period-6`}
                value="p6"
                className="portal-headcount__radio portal-headcount__radio--p6"
                defaultChecked
            />
            <input
                type="radio"
                name={`${baseId}-headcount-period`}
                id={`${baseId}-headcount-period-12`}
                value="p12"
                className="portal-headcount__radio portal-headcount__radio--p12"
            />

            <div className="portal-headcount__controls">
                <div className="portal-headcount__toggle portal-headcount__toggle--scope">
                    <label
                        htmlFor={`${baseId}-headcount-scope-nope`}
                        className="portal-headcount__toggle-btn portal-headcount__toggle-btn--nope"
                    >
                        {c.scopeNope(lang)}
                    </label>
                    <label
                        htmlFor={`${baseId}-headcount-scope-play`}
                        className="portal-headcount__toggle-btn portal-headcount__toggle-btn--play"
                    >
                        {c.scopePlay(lang)}
                    </label>
                    <label
                        htmlFor={`${baseId}-headcount-scope-np`}
                        className="portal-headcount__toggle-btn portal-headcount__toggle-btn--np"
                    >
                        {c.scopeNp(lang)}
                    </label>
                    <label
                        htmlFor={`${baseId}-headcount-scope-total`}
                        className="portal-headcount__toggle-btn portal-headcount__toggle-btn--total"
                    >
                        {c.scopeTotal(lang)}
                    </label>
                </div>
                {hasChart && (
                    <div className="portal-headcount__toggle portal-headcount__toggle--period">
                        <label
                            htmlFor={`${baseId}-headcount-period-6`}
                            className="portal-headcount__toggle-btn portal-headcount__toggle-btn--p6"
                        >
                            {c.period6(lang)}
                        </label>
                        <label
                            htmlFor={`${baseId}-headcount-period-12`}
                            className="portal-headcount__toggle-btn portal-headcount__toggle-btn--p12"
                        >
                            {c.period12(lang)}
                        </label>
                    </div>
                )}
            </div>

            {SCOPES.map((scope) => (
                <div
                    key={scope}
                    className={`portal-headcount__panel portal-headcount__panel--${scope}`}
                >
                    <ScopePanel stats={data.byScope[scope]} lang={lang} />
                </div>
            ))}

            {data.byCompany.length > 0 && (
                <div className="portal-headcount-card__chips">
                    {data.byCompany.map((co) => (
                        <span
                            key={co.companyId}
                            className="portal-headcount-card__chip"
                            title={co.name}
                        >
                            <span className="portal-headcount-card__chip-name">
                                {co.slug.toUpperCase()}
                            </span>{' '}
                            <span className="portal-headcount-card__chip-count">{co.total}</span>
                        </span>
                    ))}
                </div>
            )}

            {hasChart && (
                <div className="portal-headcount__charts">
                    <div className="portal-headcount__chart-layer portal-headcount__chart-layer--p6">
                        <TrendChart rows={rows6} companies={data.byCompany} />
                    </div>
                    <div className="portal-headcount__chart-layer portal-headcount__chart-layer--p12">
                        <TrendChart rows={rows12} companies={data.byCompany} />
                    </div>
                </div>
            )}
        </div>
    )
}
